/**
 * Zonenplan: die Einteilung der Baustelle in Schwarz-, Grau- und
 * Weissbereich, mit Durchgaengen, Schleuse, Zwischenlager, BE-Flaeche,
 * Rettungsweg und Sammelplatz.
 *
 * Reine Funktion, kein DOM, kein jsPDF. Vorbild: Kap. 7.2.2 des Musterplans.
 *
 * ═══ WARUM DIE ZONEN INEINANDER LIEGEN UND NICHT NEBENEINANDER ════════════
 * Ein Zonenplan, der die drei Bereiche als drei Kaesten nebeneinander zeigt,
 * laesst offen, was der Graubereich eigentlich ist. Er ist kein dritter Ort,
 * sondern die GRENZE: jeder Weg aus dem Schwarzbereich fuehrt durch ihn.
 * Deshalb liegt der Schwarzbereich innen, der Graubereich als Ring darum und
 * der Weissbereich aussen. Aus der Geometrie folgt dann von selbst, dass es
 * keinen Weg nach draussen gibt, der den Graubereich auslaesst — und genau
 * das ist die Aussage, um die es geht.
 *
 * ═══ WARUM JEDER PFEIL NACH AUSSEN ZEIGT ══════════════════════════════════
 * Die Richtung, die etwas verschleppen kann, ist die von innen nach aussen.
 * Ein Pfeil, der in den Schwarzbereich hineinzeigt, waere eine gedruckte
 * Einladung, die Schleuse in der falschen Richtung zu benutzen. Diese
 * Zeichnung erzeugt deshalb ausschliesslich auswaerts gerichtete Pfeile —
 * Durchgaenge wie Rettungsweg. Der Test prueft das namentlich.
 *
 * ═══ WAS SIE NICHT TUT ════════════════════════════════════════════════════
 * Sie ist schematisch und ohne Massstab; die Bereichsgrenzen werden vor Ort
 * abgesteckt. Und sie erfindet nichts: ein nicht benannter Sammelplatz
 * erscheint als offener Punkt in Rot, nicht als leere Flaeche.
 */

import type { Primitive, Zeichnung } from './primitive';
import { kasten, umbruch } from './primitive';

export type ZonenSeite = 'oben' | 'unten' | 'links' | 'rechts';

/** Ein gekennzeichneter Durchgang durch die Absperrung. */
export interface ZonenDurchgang {
    name: string;
    /**
     * `schleuse`  — Personendurchgang ueber die Schwarz-Weiss-Anlage,
     * `personen`  — sonstiger Personendurchgang (Notausstieg),
     * `fahrzeug`  — Ein- und Ausfahrt mit Reifenwaschung.
     */
    art: 'schleuse' | 'personen' | 'fahrzeug';
    seite: ZonenSeite;
}

export interface ZonenplanParameter {
    schwarz_name: string;
    /** Kennungen der Arbeitsbereiche im Schwarzbereich (nur Beschriftung). */
    schwarz_bereiche: string[];
    grau_name: string;
    weiss_name: string;
    /** Art der Absperrung — Bauzaun, Bake, Kennzeichnung. */
    absperrung: string;
    durchgaenge: ZonenDurchgang[];
    /** Zwischenlager fuer belastetes Material. Leer = keines vorgesehen. */
    zwischenlager: string;
    be_flaeche: string;
    sammelplatz: string;
    rettungsweg: string;
}

/**
 * Vorbelegung: der Regelfall einer abgesperrten Aushubflaeche mit einer
 * Schwarz-Weiss-Anlage und einer Fahrzeugausfahrt.
 *
 * `sammelplatz` bleibt LEER. Einen Sammelplatz vorzubelegen hiesse, eine
 * Festlegung zu erfinden, die nur oertlich getroffen werden kann — und sie
 * saehe im Plan aus wie eine getroffene.
 */
export const ZONENPLAN_STANDARD: ZonenplanParameter = {
    schwarz_name: 'Schwarzbereich — kontaminierter Arbeitsbereich',
    schwarz_bereiche: [],
    grau_name: 'Übergangsbereich (Grau)',
    weiss_name: 'Weißbereich — Baustelleneinrichtung',
    absperrung: 'Bauzaun umlaufend, Verbotszeichen „Zutritt verboten“',
    durchgaenge: [
        { name: 'Personendurchgang über die Schwarz-Weiß-Anlage', art: 'schleuse', seite: 'links' },
        { name: 'Fahrzeugausfahrt mit Reifenwaschung', art: 'fahrzeug', seite: 'unten' },
    ],
    zwischenlager: 'Zwischenlager belastetes Material',
    be_flaeche: 'BE-Fläche, Tagesunterkunft, Lager',
    sammelplatz: '',
    rettungsweg: 'Rettungsweg zum Sammelplatz',
};

// ─── Geometrie in Millimetern ───────────────────────────────────────────────

const RAND = 14;
const AUSSEN_B = 200;
const AUSSEN_H = 120;
/** Breite des Weissbereichs links und rechts — dort steht die Beschriftung. */
const RING_X = 40;
const RING_Y = 16;
/** Breite des Graubereichs (des Rings). */
const RING_G = 14;
/** Wie weit ein Pfeil in den Schwarzbereich hineinreicht bzw. hinausfuehrt. */
const PFEIL_INNEN = 8;
const PFEIL_AUSSEN = RING_G + 10;

interface Rechteck { x: number; y: number; b: number; h: number }

/** Ein Punkt auf der Absperrung samt Richtung nach draussen. */
const randPunkt = (seite: ZonenSeite, s: Rechteck, anteil: number) => {
    switch (seite) {
        case 'oben': return { px: s.x + s.b * anteil, py: s.y, nx: 0, ny: -1 };
        case 'unten': return { px: s.x + s.b * anteil, py: s.y + s.h, nx: 0, ny: 1 };
        case 'links': return { px: s.x, py: s.y + s.h * anteil, nx: -1, ny: 0 };
        default: return { px: s.x + s.b, py: s.y + s.h * anteil, nx: 1, ny: 0 };
    }
};

const ART_TEXT: Record<ZonenDurchgang['art'], string> = {
    schleuse: 'Schwarz-Weiß-Anlage',
    personen: 'Personendurchgang',
    fahrzeug: 'Fahrzeuge — Reifenwaschung',
};

export const zeichneZonenplan = (p: ZonenplanParameter): Zeichnung => {
    const teile: Primitive[] = [];

    const weiss: Rechteck = { x: RAND, y: RAND, b: AUSSEN_B, h: AUSSEN_H };
    const grau: Rechteck = {
        x: weiss.x + RING_X, y: weiss.y + RING_Y,
        b: weiss.b - 2 * RING_X, h: weiss.h - 2 * RING_Y,
    };
    const schwarz: Rechteck = {
        x: grau.x + RING_G, y: grau.y + RING_G,
        b: grau.b - 2 * RING_G, h: grau.h - 2 * RING_G,
    };
    const mitteX = schwarz.x + schwarz.b / 2;
    const mitteY = schwarz.y + schwarz.h / 2;

    // ── die drei Zonen ─────────────────────────────────────────────────────
    teile.push({
        art: 'rechteck', x: weiss.x, y: weiss.y, b: weiss.b, h: weiss.h,
        fuellung: 'weissbereich', linie: 'linie',
    });
    teile.push({
        art: 'rechteck', x: grau.x, y: grau.y, b: grau.b, h: grau.h,
        fuellung: 'graubereich', linie: 'linie',
    });
    // Die Absperrung IST der Rand des Schwarzbereichs — deshalb keine zweite
    // Linie daneben, sondern dieselbe, gestrichelt und in Warnfarbe.
    teile.push({
        art: 'rechteck', x: schwarz.x, y: schwarz.y, b: schwarz.b, h: schwarz.h,
        fuellung: 'schwarzbereich', linie: 'warnung', strich: 'gestrichelt',
    });

    teile.push({
        art: 'text', x: weiss.x + 3, y: weiss.y + weiss.h - 3,
        text: p.weiss_name || 'Weißbereich', groesse: 7, fett: true,
        anker: 'links', farbe: 'text',
    });
    teile.push({
        art: 'text', x: grau.x + 3, y: grau.y + 5,
        text: p.grau_name || 'Übergangsbereich (Grau)', groesse: 6.5, fett: true,
        anker: 'links', farbe: 'text',
    });
    teile.push({
        art: 'text', x: mitteX, y: schwarz.y + 10,
        text: p.schwarz_name || 'Schwarzbereich', groesse: 7.5, fett: true,
        anker: 'mitte', farbe: 'text_hell',
    });
    if (p.schwarz_bereiche.length > 0) {
        teile.push({
            art: 'text', x: mitteX, y: schwarz.y + 16,
            text: `Arbeitsbereiche: ${p.schwarz_bereiche.join(', ')}`,
            groesse: 6.5, anker: 'mitte', farbe: 'text_hell',
        });
    }
    teile.push({
        art: 'text', x: schwarz.x, y: schwarz.y - 2.5,
        text: `Absperrung: ${p.absperrung || 'OFFEN — nicht beschrieben'}`,
        groesse: 6, anker: 'links', farbe: 'warnung',
    });

    // ── Zwischenlager: im Schwarzbereich, nicht auf der BE-Flaeche ─────────
    if (p.zwischenlager) {
        teile.push(...kasten(schwarz.x + 4, schwarz.y + schwarz.h - 16, 44, 12, 'flaeche_hell',
            umbruch(p.zwischenlager, 26).slice(0, 2).map(t => ({ text: t, groesse: 5.5 })),
            'warnung'));
    }

    // ── BE-Flaeche im Weissbereich ─────────────────────────────────────────
    if (p.be_flaeche) {
        teile.push(...kasten(weiss.x + 3, weiss.y + 3, 46, 13, 'weissbereich',
            umbruch(p.be_flaeche, 26).slice(0, 2).map(t => ({ text: t, groesse: 5.5 })),
            'linie'));
    }

    // ── Durchgaenge ────────────────────────────────────────────────────────
    // Mehrere Durchgaenge auf derselben Seite werden gleichmaessig verteilt,
    // damit sie einander nicht ueberdecken.
    let maxY = weiss.y + weiss.h;
    const seiten: ZonenSeite[] = ['oben', 'unten', 'links', 'rechts'];
    for (const seite of seiten) {
        const drauf = p.durchgaenge.filter(d => d.seite === seite);
        drauf.forEach((d, i) => {
            const anteil = (i + 1) / (drauf.length + 1);
            const { px, py, nx, ny } = randPunkt(seite, schwarz, anteil);

            // Die Oeffnung in der Absperrung, quer ueber den Graubereich.
            const laengs = 34;
            const quer = RING_G;
            const mx = px + nx * quer / 2;
            const my = py + ny * quer / 2;
            const bb = Math.abs(nx) > 0 ? quer : laengs;
            const hh = Math.abs(nx) > 0 ? laengs : quer;
            teile.push({
                art: 'rechteck', x: mx - bb / 2, y: my - hh / 2, b: bb, h: hh,
                fuellung: 'weissbereich', linie: 'warnung',
            });

            // Der Pfeil zeigt IMMER aus dem Schwarzbereich heraus.
            const ax = px + nx * PFEIL_AUSSEN;
            const ay = py + ny * PFEIL_AUSSEN;
            teile.push({
                art: 'pfeil',
                x1: px - nx * PFEIL_INNEN, y1: py - ny * PFEIL_INNEN,
                x2: ax, y2: ay,
                linie: 'warnung', staerke: 0.7, spitze: 3.5,
            });

            const zeilen = umbruch(`${d.name || ART_TEXT[d.art]} (${ART_TEXT[d.art]})`, 24)
                .slice(0, 3);
            zeilen.forEach((t, z) => {
                const versatz = seite === 'oben'
                    ? -(zeilen.length - 1 - z) * 3.4 - 2
                    : seite === 'unten' ? z * 3.4 + 4
                        : (z - (zeilen.length - 1) / 2) * 3.4 + 1;
                teile.push({
                    art: 'text',
                    x: seite === 'links' ? ax - 2 : seite === 'rechts' ? ax + 2 : ax,
                    y: ay + versatz,
                    text: t, groesse: 5.5, fett: z === 0,
                    anker: seite === 'links' ? 'rechts' : seite === 'rechts' ? 'links' : 'mitte',
                    farbe: 'text',
                });
                maxY = Math.max(maxY, ay + versatz);
            });
        });
    }

    // ── Rettungsweg und Sammelplatz ────────────────────────────────────────
    // Der Rettungsweg fuehrt aus dem Schwarzbereich HERAUS zum Sammelplatz im
    // Weissbereich. Auch er wird nie nach innen gezeichnet.
    //
    // Der Kasten liegt GANZ im Weissbereich, unterhalb des Graurings — so
    // sagen es Legende und Fussnote, und die Zeichnung darf sich nicht selbst
    // widersprechen: ein Sammelplatz, der in den Uebergangsbereich hineinragt,
    // waere ein Sammelplatz hinter der Absperrung. Neben den Ring passt er
    // nicht: der ist nur RING_X breit, der Kasten breiter. Der Test haelt das
    // fest.
    const zielB = 44;
    const zielH = 14;
    const zielX = weiss.x + weiss.b - zielB - 2;
    const zielY = grau.y + grau.h + 1;
    teile.push({
        art: 'pfeil',
        x1: mitteX + 10, y1: mitteY + 6,
        x2: zielX - 2, y2: zielY + 2,
        linie: 'hinweis', staerke: 0.7, spitze: 3.5,
    });
    teile.push({
        art: 'text', x: (mitteX + 10 + zielX) / 2, y: (mitteY + 6 + zielY) / 2 - 1.5,
        text: p.rettungsweg || 'Rettungsweg', groesse: 5.5, fett: true,
        anker: 'mitte', farbe: 'hinweis',
    });

    if (p.sammelplatz) {
        teile.push(...kasten(zielX, zielY, zielB, zielH, 'weissbereich',
            [{ text: 'Sammelplatz', groesse: 6, fett: true },
                ...umbruch(p.sammelplatz, 24).slice(0, 2).map(t => ({ text: t, groesse: 5.5 }))],
            'hinweis'));
    } else {
        teile.push(...kasten(zielX, zielY, zielB, zielH, 'weissbereich',
            [{ text: 'OFFEN: kein', groesse: 6, fett: true, farbe: 'warnung' },
                { text: 'Sammelplatz benannt', groesse: 5.5, farbe: 'warnung' }],
            'warnung'));
    }

    const fussnoten: string[] = [
        'Schematische Darstellung ohne Maßstab. Die Lage der Bereichsgrenzen ist vor Beginn '
        + 'der Arbeiten örtlich abzustecken und dauerhaft zu kennzeichnen.',
        'Der Schwarzbereich wird ausschließlich über die gekennzeichneten Durchgänge '
        + 'verlassen — Personen über die Schwarz-Weiß-Anlage, Fahrzeuge über die '
        + 'Reifenwaschung.',
    ];
    if (p.zwischenlager) {
        fussnoten.push('Das Zwischenlager für belastetes Material liegt im Schwarzbereich. '
            + 'Eine Lagerung auf der BE-Fläche wäre eine Verschleppung in den Weißbereich.');
    }
    if (!p.sammelplatz) {
        fussnoten.push('OFFEN: Es ist kein Sammelplatz im Weißbereich benannt — der '
            + 'Rettungsweg endet damit nirgends.');
    }

    return {
        breite: weiss.x + weiss.b + RAND,
        hoehe: Math.max(weiss.y + weiss.h, maxY) + RAND,
        titel: 'Zonenplan — Einteilung in Schutzzonen',
        untertitel: 'Schwarz-, Grau- und Weißbereich mit Durchgängen, Rettungsweg und Sammelplatz',
        teile,
        legende: [
            { farbe: 'schwarzbereich', text: 'Schwarzbereich — kontaminiert, Zutritt nur mit PSA' },
            { farbe: 'graubereich', text: 'Übergangs-/Graubereich — Schleuse, Reinigung' },
            { farbe: 'weissbereich', text: 'Weißbereich — unbelastet, BE-Fläche, Sammelplatz' },
        ],
        fussnoten,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// PRUEFUNG DER PARAMETER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prueft den Zonenplan auf fachliche Luecken.
 *
 * Wie bei der Schleuse getrennt von der Zeichenfunktion: gezeichnet wird
 * auch der unvollstaendige Stand, damit sichtbar ist, WAS fehlt.
 */
export const pruefeZonenplan = (p: ZonenplanParameter): string[] => {
    const fehler: string[] = [];

    if (!p.durchgaenge.some(d => d.art === 'schleuse')) {
        fehler.push('Ohne Schwarz-Weiß-Anlage gibt es keinen zulässigen Weg aus dem '
            + 'Schwarzbereich — ein Durchgang der Art „Schleuse“ fehlt.');
    }
    if (p.durchgaenge.length === 0) {
        fehler.push('Es ist kein Durchgang durch die Absperrung gekennzeichnet.');
    }
    if (!p.absperrung.trim()) {
        fehler.push('Die Absperrung des Schwarzbereichs ist nicht beschrieben.');
    }
    if (!p.sammelplatz.trim()) {
        fehler.push('Es ist kein Sammelplatz im Weißbereich benannt.');
    }
    if (!p.rettungsweg.trim()) {
        fehler.push('Der Rettungsweg aus dem Schwarzbereich ist nicht beschrieben.');
    }

    return fehler;
};
